import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, AlertApiRecord } from "../api/rest";
import { relativeTime } from "../stores/telemetryStore";
import { useSiteId } from "../hooks/useSiteId";
import { SplitView } from "../app/layout/SplitView";
import styles from "./FleetPage.module.css";
import tabStyles from "./AlertsPage.module.css";
import panel from "../panels/Panel.module.css";

type TabStatus = "open" | "ack" | "resolved";

const SEV_CLASS: Record<AlertApiRecord["severity"], string> = {
  CRIT: styles.pill_linkdown,
  WARN: styles.pill_idle,
  INFO: styles.pill_empty,
};

function tsToMs(iso: string): number {
  return new Date(iso).getTime();
}

function AlertDetail({ alert, onAck }: { alert: AlertApiRecord | null; onAck: (id: string) => void }) {
  const { t } = useTranslation();
  if (!alert) {
    return (
      <aside className={panel.panel} aria-label={t("inspector.alert")}>
        <h2 className={panel.title}>{t("inspector.alert")}</h2>
        <p className={panel.empty}>{t("alerts.selectPrompt")}</p>
      </aside>
    );
  }
  return (
    <aside className={panel.panel} aria-label={t("inspector.alert")}>
      <h2 className={panel.title}>{t("inspector.alert")}</h2>
      <dl className={panel.dl}>
        <dt>{t("alerts.col.severity")}</dt>
        <dd><span className={`${styles.pill} ${SEV_CLASS[alert.severity]}`}>{t(`alerts.sev.${alert.severity}`)}</span></dd>
        <dt>{t("alerts.col.code")}</dt>
        <dd className={panel.mono}>{alert.code}</dd>
        <dt>{t("common.col.asset")}</dt>
        <dd className={panel.mono}>{alert.assetId ?? "—"}</dd>
        <dt>{t("inspector.time")}</dt>
        <dd className={panel.mono}>{relativeTime(tsToMs(alert.ts))}</dd>
        <dt>{t("common.col.status")}</dt>
        <dd className={panel.mono}>{alert.status}</dd>
        {/* ADR 299: a resolved alert says WHY it closed — a condition that
            cleared and a subject that was deleted are different facts, and
            `ts` above stays the last time the breach was measured. */}
        {alert.status === "resolved" && alert.resolvedAt && (
          <>
            <dt>{t("alerts.col.resolved")}</dt>
            <dd className={panel.mono}>{relativeTime(tsToMs(alert.resolvedAt))}</dd>
            {alert.resolvedReason && (
              <>
                <dt>{t("alerts.col.reason")}</dt>
                <dd>{t(`alerts.reason.${alert.resolvedReason}`)}</dd>
              </>
            )}
          </>
        )}
        {alert.detail && Object.keys(alert.detail).length > 0 && (
          <>
            <dt>{t("inspector.detail")}</dt>
            <dd className={panel.mono} style={{ fontSize: 11, wordBreak: "break-all" }}>{JSON.stringify(alert.detail)}</dd>
          </>
        )}
      </dl>
      {alert.status === "open" && (
        <button className={panel.btn} style={{ marginTop: "var(--space-3)", width: "100%" }}
                onClick={() => onAck(alert.id)}>
          {t("alerts.action.ack")}
        </button>
      )}
    </aside>
  );
}

export function AlertsPage() {
  const SITE_ID = useSiteId();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabStatus>("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["alerts", SITE_ID, tab],
    queryFn: () => api.listAlerts(SITE_ID, tab),
    refetchInterval: tab === "open" ? 15_000 : false,
    retry: 0,
  });

  const items = data ?? [];
  const selected = items.find((a) => a.id === selectedId) ?? null;

  const handleAck = (id: string) => {
    api.ackAlert(id)
      .then(() => qc.invalidateQueries({ queryKey: ["alerts", SITE_ID] }))
      .catch(console.error);
  };

  const table = (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t("alerts.col.severity")}</th>
            <th>{t("alerts.col.code")}</th>
            <th>{t("alerts.col.asset")}</th>
            <th>{t("alerts.col.when")}</th>
            {tab === "open" && <th />}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 3 }, (_, i) => (
                <tr key={i}>
                  {[60, 100, 90, 80, tab === "open" ? 70 : 0].filter(Boolean).map((w, j) => (
                    <td key={j}><span className="skeleton" style={{ width: w, height: 14, display: "block" }} /></td>
                  ))}
                </tr>
              ))
            : isError
              ? <tr><td colSpan={tab === "open" ? 5 : 4} className={styles.error}>{t("alerts.error")}</td></tr>
              : items.length > 0
                ? items.map((a) => (
                    <tr key={a.id} tabIndex={0} onClick={() => setSelectedId(a.id)}
                        aria-selected={a.id === selectedId}
                        className={a.id === selectedId ? styles.rowSelected : undefined}>
                      <td>
                        <span className={`${styles.pill} ${SEV_CLASS[a.severity]}`}>
                          {t(`alerts.sev.${a.severity}`)}
                        </span>
                      </td>
                      <td className={styles.mono}>{a.code}</td>
                      <td className={styles.mono}>{a.assetId ?? "—"}</td>
                      <td className={styles.mono}>{relativeTime(tsToMs(a.ts))}</td>
                      {tab === "open" && (
                        <td>
                          <button
                            className={tabStyles.ackBtn}
                            onClick={(e) => { e.stopPropagation(); handleAck(a.id); }}
                          >
                            {t("alerts.action.ack")}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                : <tr>
                    <td colSpan={tab === "open" ? 5 : 4} className={styles.empty}>
                      {t("alerts.page.empty")}
                    </td>
                  </tr>
          }
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={`${styles.page} ${styles.wide}`}>
      <h1 className={styles.h1}>{t("alerts.title")}</h1>

      <div className={tabStyles.tabs} role="tablist">
        {(["open", "ack", "resolved"] as TabStatus[]).map((s) => (
          <button
            key={s}
            role="tab"
            aria-selected={tab === s}
            className={tab === s ? tabStyles.tabActive : tabStyles.tab}
            onClick={() => setTab(s)}
          >
            {t(`alerts.tab.${s}`)}
          </button>
        ))}
      </div>

      <p className={styles.sub}>
        {isLoading
          ? <span className="skeleton" style={{ width: 80, height: 16 }} />
          : `${items.length} ${t(`alerts.tab.${tab}`).toLowerCase()}`}
      </p>

      <SplitView list={table} detail={<AlertDetail alert={selected} onAck={handleAck} />}
                 detailOpen={!!selected} detailLabel={t("inspector.alert")} />
    </div>
  );
}
